import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from 'bootstrap';

import { CompanyRoleFormModal } from './CompanyRoleFormModal'; 

import { apiRequest, sendEvent } from '../../utils';

import type { ClaimRequest, Contact, ListType, Member, KybContact } from '../../types';
import { CompanyMembersList } from './CompanyMembersList';

/**
 * Helper function to determine if a contact has been submitted
 * A contact is considered submitted if it has both email and phone
 */
const isContactSubmitted = (contact: Contact): boolean => {
  return !!(contact.email && contact.telefono);
};

const initialContact: Contact = {
  nombre: "",
  cargo: "",
  email: "",
  telefono: "",
  beneficiario: false,
  porcentaje_beneficio: "0.0",
  claim_request_id: 0,
  nacionalidad: "",
  director_nominal: false,
  pep: false,
  pep_cargo: "",
  pep_entidad: "",
  pep_fecha_inicio: ""
};

/**
 * this component do 2 thing and act as a miniwizard rendering CompanyMemberList
 * it will derived 2 data (rolesData y BeneficiaryData aka accionistas) 
 * derived from the props members and contacts (kyb_contacts)
 * NOTE: talvez refactorizar spliteando esto en 2 pasos CompanyMembersRoles y CompanyMembersBeneficiaries
 * y usar context para todo el kyb flow, asi cada estado y fn le pertenece
 * a benefits o roles
 */
export const CompanyMembers: React.FC<CompanyMembersProps> = ({ 
  members, 
  contacts, 
  organization, 
  claimRequest,
  privacyPolicyAccepted,
  onStepCompleted,
}) => {
  const [currentStep, setCurrentStep] = useState(1); // Track the current step
  
  const [rolesData, setRolesData] = useState<Contact[]>([]);
  const [beneficiariesData, setBeneficiariesData] = useState<Contact[]>([]);

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [selectedMember, setSelectedMember] = useState<Contact>(initialContact);

  useEffect(() => {
    let computedRoles: Contact[] = [];

    if (contacts.length > 0) {
      // If contacts that are no beneficiario as cargo length is not equal to members length, then add the members to the computedRoles then update the computedRoles with the contacts
      if (contacts.filter(role => role.cargo !== 'beneficiario').length !== members.length) {
        computedRoles = members.map(member => {
          const foundContact = contacts?.find(c => c.nombre === member.nombre && c.cargo === member.cargo) ?? {};
          const contactData = Object.keys(foundContact).length > 0 ? foundContact : {};
  
          return {
            ...initialContact,
            ...member,
            claim_request_id: claimRequest.id,
            ...contactData
          };
        });

      } else {
        computedRoles = contacts.map(contact => contact);
      }
    } else {
      computedRoles = members.map(member => {
        const foundContact = contacts?.find(c => c.nombre === member.nombre && c.cargo === member.cargo) ?? {};
        const contactData = Object.keys(foundContact).length > 0 ? foundContact : {};

        return {
          ...initialContact,
          ...member,
          claim_request_id: claimRequest.id,
          ...contactData
        };
      });
    }
    
    const computedRolesData: Contact[] = computedRoles.filter(role => role.cargo !== 'beneficiario');
    const computedBeneficiariesData: Contact[] = computedRoles.filter(role => role.beneficiario);

    setRolesData(computedRolesData);
    setBeneficiariesData(computedBeneficiariesData);
  }, [members, contacts, claimRequest.id]);

  const globalStake = useMemo(() => {
    return beneficiariesData.reduce((acc, beneficiary: Contact) => {
      return acc + parseFloat(beneficiary.porcentaje_beneficio.toString());
    }, 0);
  }, [beneficiariesData]);


  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <CompanyMembersList
            organization={organization}
            currentStep={currentStep}
            membersData={rolesData}
            handleSumBeneficiaries={() => 0}
            checkNextStep={disableNextStep}
            onModalOpen={handleModalOpen}
            onDeleteBeneficiary={handleRemoveBeneficiary}
            onNextStepClick={handleNextStepClick(2)} 
            onPreviousStepClick={handlePreviousStepClick}
          />
        );
      case 2:
        return (
          <CompanyMembersList
            privacyPolicyAccepted={privacyPolicyAccepted}
            organization={organization}
            currentStep={currentStep}
            membersData={beneficiariesData}
            isBeneficiary
            handleSumBeneficiaries={handleSumBeneficiaries}
            checkNextStep={disableNextStep}
            onDeleteBeneficiary={handleRemoveBeneficiary}
            onModalOpen={handleModalOpen}
            onNextStepClick={handleNextStepClick(3)}
            onPreviousStepClick={handlePreviousStepClick}
          />
        );
      // no case 3 and for it end in 2 so 2 on next Step click debe llamar onStepCompleted
      // case 3:
      //   return (
      //     // TODO this step hacia afuera
      //     <PrivacyPolicies
      //       organization={organization}
      //       currentUser={currentUser}
      //       currentStep={currentStep}
      //       isSubmitting={submitting}
      //       onNextClick={handleNextStepClick}
      //       claimRequestId={claimRequest ? claimRequest.id : null}
      //       privacyPolicyAccepted={privacyPolicyAccepted}
      //     />
      //   );
      // case 4:
      //   return (
      //     <KybStepSuccess
      //       description={t('kyb_flow.phone_confirmation.success.description')}
      //       onStepCompleted={onStepCompleted}
      //     />
      //   );
      default:
        return null;
    }
  };

  const handleNextStepClick = (toStep: number) => () => {
    if(toStep === 2) {
      // Compute beneficiaries data when moving to step 2
      const nonMemberContacts = contacts.filter(contact =>
        !members.some(member =>
          member.nombre === contact.nombre && member.cargo === contact.cargo
        ) && contact.beneficiario
      );

      const beneficiaries = [...rolesData.filter(member => member.beneficiario), ...nonMemberContacts];
      setBeneficiariesData(beneficiaries);
      setCurrentStep(2);
      return;
    }

    if(toStep === 3) {
      const payload = { 
        event_name: "KYB Members", 
        properties: { 
          nombre: organization.nombre,
          miembros_check: true,
          accionistas_check: true,
        }
      };

      sendEvent(payload);
      onStepCompleted();
      return;
    }

    setCurrentStep(toStep);
  };

  const handlePreviousStepClick = () => {
    setCurrentStep((prevStep) => { return prevStep - 1 })
  }

  const handleModalOpen = (index: number, list: ListType = 'rolesData') => {
    let memberObject: Contact = {
      ...initialContact,
      claim_request_id: claimRequest.id,
    };

    if (index >= 0 && list === 'rolesData') memberObject = { ...rolesData[index] };
    if (index >= 0 && list === 'beneficiariesData') memberObject = { ...beneficiariesData[index] };

    if (!Object.keys(memberObject).includes('pep') || index < 0) {
      memberObject = {
        ...memberObject,
        beneficiario: null,
        nacionalidad: "",
        director_nominal: null,
        pep: null,
        pep_cargo: "",
        pep_entidad: "",
        pep_fecha_inicio: ""
      };
    }
    
    setSelectedMember(memberObject);
    setSelectedIndex(index);

    const modalElement = document.getElementById('roleModal');
    const modal = Modal.getOrCreateInstance(modalElement as Element);
    modal.show();
  };

  const handleSumBeneficiaries = (data = [], force = false) => {
    const beneficiaries = (data.length > 0 || force) ? data : beneficiariesData;
    return beneficiaries.reduce((acc, member) => acc + parseFloat(member.porcentaje_beneficio.toString()), 0);
  };

  const handleRemoveBeneficiary = (index: number) => {
    const beneficiary = beneficiariesData[index];

    const isMember = members.some(member =>
      member.nombre === beneficiary.nombre && member.cargo === beneficiary.cargo
    );

    if (isMember) {
      const updatedMember = {
        ...beneficiary,
        beneficiario: false,
        porcentaje_beneficio: "0.0"
      };

      onMemberUpdate(index, {kyb_contact: updatedMember});
    } else {
      onMemberDelete(index, beneficiary);
    }
  };

  const disableNextStep = () => {
    if (currentStep === 1) {
      return rolesData.filter(member => isContactSubmitted(member)).length !== rolesData.length;
    } else if (currentStep === 2) {
      const sum = handleSumBeneficiaries();
      return !(sum > 99 && sum <= 100);
    }
    return false;
  }

  const onMemberUpdate = (index: number, updatedMember: {kyb_contact: Contact}) => {
    onMemberSubmit(index, { kyb_contact: { ...updatedMember.kyb_contact, claim_request_id: claimRequest.id } });
  };

  // metodo para actualizar un miembro
  const onMemberSubmit = async (index: number, updatedMember: KybContact) => {
    try {
      const res = await apiRequest('/business_claim/kyb_contact', 'POST', updatedMember);

      if (res.success) {
        let newState = {
          beneficiariesData: beneficiariesData,
          rolesData: rolesData,
        };
        
        // if index is negative, add the member to the beneficiariesData
        if (index < 0) {
          newState = {
            ...newState,
            beneficiariesData: [...beneficiariesData, { ...res.kyb_contact, isSubmitted: true }]
          };
        }
        // if the member is not a beneficiary, check if exists in the beneficiariesData by email and remove it
        else if (!updatedMember.kyb_contact.beneficiario) {
          const beneficiaryIndex = beneficiariesData.findIndex(beneficiary => beneficiary.email === updatedMember.kyb_contact.email);

          if (beneficiaryIndex >= 0) {
            newState = {
              ...newState,
              beneficiariesData: beneficiariesData.filter((_, i) => i !== beneficiaryIndex)
            };
          }
          // if the member cargo is not beneficiario, find the recordIndex and update rolesData record
          if (updatedMember.kyb_contact.cargo !== 'beneficiario') {
            newState = {
              ...newState,
              rolesData: rolesData.map((item, i) => 
                i === index ? { ...res.kyb_contact } : item
              )
            };
          }
        }
        // if the member is a beneficiary, check if exists in the beneficiariesData by email and update it, also if member cargo is not beneficiario, update rolesData
        else if (updatedMember.kyb_contact.beneficiario) {
          const beneficiaryIndex = beneficiariesData.findIndex(beneficiary => beneficiary.email === updatedMember.kyb_contact.email);
          if (beneficiaryIndex >= 0) {
            newState = {
              ...newState,
              beneficiariesData: beneficiariesData.map((item, i) => 
                i === beneficiaryIndex ? { ...res.kyb_contact } : item
              )
            };
          }
          // if the member cargo is not beneficiario, find the recordIndex and update rolesData record
          if (updatedMember.kyb_contact.cargo !== 'beneficiario') {
            newState = {
              ...newState,
              rolesData: rolesData.map((item, i) => 
                i === index ? { ...res.kyb_contact, isSubmitted: true } : item
              )
            };
          }
        }
        /**
         * pueda q hay un mismath aqui, beneficiariesData y rolesData no son iguales
         * son derivados del prop members
         *  */ 
        setBeneficiariesData(newState.beneficiariesData);
        setRolesData(newState.rolesData);
        setSelectedIndex(null);
        setSelectedMember(initialContact);
      }
    } catch (error) {
      console.error("Error al enviar datos del miembro", error);
    }
  };
  
  // metodo para eliminar un beneficiario
  const onMemberDelete = async (index: number, beneficiary: Contact) => {
    try {
      const res = await apiRequest(`/business_claim/kyb_contact/${beneficiary.id}`, 'DELETE');
      if (res.success) {
        const updatedBeneficiaries = beneficiariesData.filter((_, i) => i !== index);
        setBeneficiariesData(updatedBeneficiaries);
      }
    } catch (error) {
      console.error("Error al eliminar el miembro", error);
    }
  };  

  return (
    <div className="row d-flex mt-5 justify-content-center" style={{ maxWidth: 1000 }}>
      {renderStepContent()}
      <CompanyRoleFormModal
        modalId="roleModal"
        index={selectedIndex ?? -1}
        member={selectedMember}
        onMemberUpdate={(index, member) => onMemberUpdate(index, {kyb_contact: member})}
        globalStake={globalStake}
      />
    </div>
  );
};
interface CompanyMembersProps {
  /** 
   * solo se usa pa pasarselo a CompanyMembersList y generar el texto btn next
   * todo: refactor context con patron builder maybe
   */
  privacyPolicyAccepted?: boolean;
  members: Member[];
  contacts: Contact[];
  organization: {
    id: number;
    nombre: string;
    [key: string]: any;
  };
  claimRequest: ClaimRequest;
  onStepCompleted: () => void;
}

